// React Query hooks for per-event stock allocations.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listEventAllocations,
  setEventAllocation,
  removeEventAllocation,
  listEventAllocationAuditEntries,
  type SetAllocationInput,
  type RemoveAllocationInput,
  updateAllocationBatch,
  type UpdateAllocationBatchInput,
} from '@/lib/api/eventAllocations';

const KEYS = {
  all: ['event-allocations'] as const,
  byEvent: (eventId: string) => [...KEYS.all, 'by-event', eventId] as const,
  audit: (eventId: string) => [...KEYS.all, 'audit', eventId] as const,
};

// ==== Queries ====

export const useEventAllocations = (eventId: string | null) =>
  useQuery({
    queryKey: KEYS.byEvent(eventId ?? ''),
    queryFn: () => (eventId ? listEventAllocations(eventId) : Promise.resolve([])),
    enabled: !!eventId,
  });

export const useEventAllocationAudit = (eventId: string | null) =>
  useQuery({
    queryKey: KEYS.audit(eventId ?? ''),
    queryFn: () =>
      eventId
        ? listEventAllocationAuditEntries(eventId, 200)
        : Promise.resolve([]),
    enabled: !!eventId,
  });

// ==== Mutations ====

export const useSetEventAllocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetAllocationInput) => setEventAllocation(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.byEvent(vars.eventId) });
      qc.invalidateQueries({ queryKey: KEYS.audit(vars.eventId) });
    },
  });
};

export const useRemoveEventAllocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RemoveAllocationInput) => removeEventAllocation(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.byEvent(vars.eventId) });
      qc.invalidateQueries({ queryKey: KEYS.audit(vars.eventId) });
    },
  });
};

export const useUpdateAllocationBatch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAllocationBatchInput) => updateAllocationBatch(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.byEvent(vars.eventId) });
      qc.invalidateQueries({ queryKey: KEYS.audit(vars.eventId) });
    },
  });
};

/** Query keys, for callers that batch several writes and invalidate once. */
export const eventAllocationKeys = KEYS;
